import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map';

import { TeComponent } from '../common/te-component';
import { theme } from '../common/theme';
import { booleanConverter } from '../common/converters';

import './te-pagination-page';
import '../te-icon';
import '../input/te-input-number';
import { IconName } from '../te-icon';

const parentElementWidth = 424;
const essentialElementWidth = 320;

/** Pagination Component */
@customElement('te-pagination')
export class TePagination extends TeComponent {
  static styles = [
    ...TeComponent.styles,
    css`
      .te-pagination {
        margin: 0;
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
      }

      te-icon {
        background: ${theme.colors.shade0};
        border-radius: 4px;
        user-select: none;
      }

      .dot {
        width: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    `,
  ];

  /** Total page count for pagination */
  @property({ type: Number }) pages = 0;
  /** Selected page for pagination */
  @property({ type: Number }) currentPage = 0;
  /** Maximum width for number input */
  @property({ type: Number }) inputMaxWidth = 72;
  /** Minimum width for nubmer input */
  @property({ type: Number }) inputMinWidth = 64;
  /** Render pagination with number input */
  @property({ type: Boolean, converter: booleanConverter })
  withNumberInput = false;
  /** Pre-styled pagination variant */
  @property({ type: String }) variant?: 'default' | 'compact' | 'essential' = 'default';

  @state() onDocumentListenFn: (e: Event) => void;
  @state() currentParentWidth = document.body.clientWidth;
  @state() value = this.currentPage;

  constructor() {
    super();
    this.onDocumentListenFn = this.onDocumentListen.bind(this);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onDocumentListenFn);
  }

  firstUpdated() {
    window.addEventListener('resize', this.onDocumentListenFn);
  }

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('currentPage')) {
      this.value = this.currentPage;
    }
  }

  onDocumentListen() {
    this.currentParentWidth = (this.parentElement || document.body).clientWidth;
  }

  shouldShow(page: number) {
    const { pages, currentPage } = this;
    const { firstSection, lastSection, middleSection } = this.getPageValues();

    if (pages <= 6) return true;
    // Always show first and last values
    const firstOrLast = page === 1 || page === pages;
    // Show first 5 and last 1 values if in the start
    const firstFiveLastOne = firstSection && (page < 6 || pages - page < 1);
    // Show first 1 and last 5 values if in the end
    const firstOneLastFive = lastSection && (page < 2 || pages - page < 5);
    // Show 3 middle values if in the middle
    const middleValues = middleSection && Math.abs(currentPage - page) <= 1;
    return firstOrLast || middleValues || firstFiveLastOne || firstOneLastFive;
  }

  getPageValues() {
    const { currentPage, pages } = this;
    return {
      firstSection: currentPage < 5,
      lastSection: pages - currentPage < 4,
      middleSection: currentPage > 4 && pages - currentPage > 3,
    };
  }

  shouldShowDots(page: number) {
    const { pages } = this;
    if (pages <= 6) return false;
    const { firstSection, lastSection, middleSection } = this.getPageValues();

    // Show ... in the second position if current page in the end
    const second = lastSection && pages - page === 5;
    // Show ... in sixth last position if current page in the start
    const sixth = firstSection && page === 6;
    // Show ... in 2nd and 2nd last if in the middle
    const firstAndSecondLast = middleSection && (page === 2 || pages - page === 1);

    return second || sixth || firstAndSecondLast;
  }

  setPage(page: number) {
    if (page > this.pages || !page) return;
    // TODO: Need to add custom event in documentation
    this.sendCustomEvent('tePageSelect', page);
  }

  onInputChange(e: CustomEvent) {
    const { value } = e.detail.target;
    const charCode = e.detail.which;
    if ((charCode > 31 && (charCode < 48 || charCode > 57)) || !value) {
      return;
    }
    this.value = value;
  }

  onInputKeyDown(e: CustomEvent) {
    if (e.detail.key === 'Enter') {
      const { value } = this;
      if (
        ['.', ','].some((char) => `${value}`.includes(char)) &&
        value >= 1 &&
        value <= this.pages
      ) {
        this.value = Math.floor(value);
      } else if (value > this.pages) {
        this.value = this.pages;
      } else if (value < 1) {
        this.value = 1;
      }
      this.setPage(this.value);
    }
  }

  getArrowStyle(arrowName: string) {
    const { currentPage, pages } = this;
    const isDisabled = currentPage === (arrowName === 'left' ? 1 : pages);
    return {
      color: isDisabled ? `${theme.colors.shade3}` : `${theme.colors.shade5}`,
      cursor: isDisabled ? 'default' : 'pointer',
    };
  }

  renderIcon(iconName: IconName, page: number, arrowName: string) {
    return html`
      <te-icon
        name=${iconName}
        size="8"
        wrapperSize="40"
        @click=${() => this.setPage(page)}
        style=${styleMap(this.getArrowStyle(arrowName))}
      ></te-icon>
    `;
  }

  render() {
    const pageArray = new Array(this.pages).fill(0).map((_v, i) => i + 1);
    const compact = this.variant === 'compact';
    const essential = this.variant === 'essential';
    return html`
      <div class="te-pagination">
        ${this.withNumberInput
          ? html`
              <te-input-number
                @teChange=${this.onInputChange}
                @teKeyDown=${this.onInputKeyDown}
                maxWidth=${this.inputMaxWidth}
                minWidth=${this.inputMinWidth}
                value=${this.value}
                align="center"
                minValue="1"
                maxValue=${this.pages}
              ></te-input-number>
            `
          : null}
        ${this.currentParentWidth < parentElementWidth || compact || essential
          ? this.renderIcon('previous', 1, 'left')
          : null}
        ${this.renderIcon('chevron-left', this.currentPage - 1, 'left')}
        ${this.currentParentWidth < parentElementWidth || compact
          ? this.currentParentWidth > essentialElementWidth
            ? html`
                <te-pagination-page ?active=${true}>
                  ${this.currentPage}
                </te-pagination-page>
              `
            : null
          : essential
          ? null
          : pageArray.map((page) => {
              if (this.shouldShowDots(page))
                return html`
                  <span class="dot">...</span>
                `;
              if (!this.shouldShow(page)) return null;
              return html`
                <te-pagination-page
                  ?active=${page === this.currentPage}
                  @click=${() => this.setPage(page)}
                >
                  ${page}
                </te-pagination-page>
              `;
            })}
        ${this.renderIcon('chevron-right', this.currentPage + 1, 'right')}
        ${this.currentParentWidth < parentElementWidth || compact || essential
          ? this.renderIcon('next', this.pages, 'right')
          : null}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'te-pagination': TePagination;
  }
}
